import './RagChat.scss';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Breadcrumb, Button, Col, Container, Form, ProgressBar, Row } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import { ChatMessage, EmbeddedLoader, Rag, RagConfigs, RagLoader } from '../../../models/LLM';
import { EmbedBody, Llm } from '../../../api/Llm';
import { useToasts } from '../../../contexts/toasts';
import IconButton from '../../../components/common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Flex, Modal, Select, Tabs, TabsProps, Tooltip } from 'antd';
import EditLoaderModal from '../../../components/generics/EditLoaderModal';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import classNames from 'classnames';
import { cloneDeep, set } from 'lodash';
import { formatDate } from '../../../utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { sectionLabelsSelector } from '../../../slices/sectionsSlice';
import { Helmet } from 'react-helmet';
import AppLoader from '../../../components/common/AppLoader';
import { v4 } from 'uuid';
import { EventEmitter } from '../../../utils/EventEmitter';
import { AppNotification } from '../../../models/Notification';
import Chat from '../../../components/generics/Chat';
import { useAuth } from '../../../contexts/auth';

function RagChat() {
  const { id } = useParams();
  const [showSettings, setShowSettings] = useState(true);
  const [error, setError] = useState(false);
  const [rag, setRag] = useState<Rag>();
  const [loaders, setLoaders] = useState<RagLoader[]>();
  const [ragConfigs, setRagConfigs] = useState<RagConfigs>();
  const [showCreateLoaderModal, setShowCreateLoaderModal] = useState(false);
  const [showEmbedLoaderModal, setShowEmbedLoaderModal] = useState(false);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState<RagLoader>(new RagLoader());
  const [usedLoaders, setUsedLoaders] = useState<number[]>([]);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const { loggedUser } = useAuth();

  const ragLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "rags"));
  const loaderLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "loaders"));
  // const apiKeyLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "api-keys"));

  const showEmbeddedLoaderActions = useMemo(() => rag?.owner.id === loggedUser?.sub, [rag, loggedUser]);

  const settingsTabs: TabsProps['items'] = useMemo(() => ([
    { key: 'loaders', label: `Manage ${loaderLabels.plural || "Loaders"}`, icon: <FontAwesomeIcon icon={solid('bars-progress')} /> },
    { key: 'context', label: 'Context Configuration', icon: <FontAwesomeIcon icon={solid('tools')} /> },
    // { key: 'api-keys', label: apiKeyLabels.plural || "API Keys", icon: <FontAwesomeIcon icon={solid('key')} /> },
  ]), [loaderLabels]);

  useEffect(() => {
    if (id && rag) {
      EventEmitter.subscribe("JOB_PROGRESS", (newNotification: AppNotification) => {
        if (+newNotification.details.resourceId === +id) {
          setRag(r => {
            const newRag = cloneDeep(r!);
            const found = r!.loaders!.findIndex(l => l.jobId === newNotification.details.jobId);
            if (found < 0) return newRag;
            newRag.loaders![found].status = {
              progress: newNotification.details.progress,
              total: newNotification.details.total,
              finished: newNotification.details.finished,
            }
            return newRag;
          })
        }
      });

      return () => EventEmitter.unsubscribe("JOB_PROGRESS");
    }
  }, [id, rag]);

  const initRag = useCallback(() => {
    Llm.getRag(id!)
      .then(
        ({ data }) => {
          setRag(data);
          setUsedLoaders(data.loaders?.map(l => l.id!) || []);
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rags-error', message, `${ragLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    // eslint-disable-next-line
  }, [id, showToast]);

  const initRagContextConfigs = useCallback(() => {
    Llm.getRagsContextConfigs()
      .then(
        ({ data }) => {
          const fixedConfigs = cloneDeep(data);
          fixedConfigs.configs.forEach(c => {
            c.value = rag!.context[c.name];
          });
          setRagConfigs(fixedConfigs);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rag-error', message, `${ragLabels.singular} Configs Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    // eslint-disable-next-line
  }, [rag, showToast]);

  const getLoaders = useCallback(() => {
    const params = new URLSearchParams();
    if (usedLoaders.length) params.set('excludeIds', usedLoaders.join(','));

    Llm.getLoaders(params)
      .then(
        ({ data }) => setLoaders((data as Array<RagLoader>)),
        (err) => {
          setLoaders([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        });
    // eslint-disable-next-line
  }, [showToast, usedLoaders]);

  useEffect(getLoaders, [getLoaders]);
  useEffect(initRag, [initRag]);

  useEffect(() => {
    if (rag && !ragConfigs) initRagContextConfigs();
  }, [rag, ragConfigs, initRagContextConfigs]);

  const reloadLoaders = useCallback(() => {
    setLoaders(undefined);
    getLoaders();
  }, [getLoaders]);

  const handleClose = useCallback(() => {
    setShowEmbedLoaderModal(false);
    setShowCreateLoaderModal(false);
    setLoader(new RagLoader());
  }, []);

  const embedLoader = useCallback((loader: RagLoader) => () => {
    showLoader();

    const body: EmbedBody = {
      jobId: v4(),
      ragId: +id!,
      loaderId: loader.id!,
      splittingConf: { "chunk_size": 1000, "chunk_overlap": 200 },
      accessLevel: rag!.accessLevel,
      organizationId: rag!.organization.id,
      ownerId: rag!.owner.id,
    };

    Llm.embedRagLoader(body)
      .then(
        () => {
          hideLoader();
          showToast('loader-embed-success', `${loaderLabels.singular} embedded successfully`, `${loaderLabels.singular} Success`, 'success');
          handleClose();
          initRag();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loader-embed-error', message, `${loaderLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, rag, showLoader, showToast, hideLoader, handleClose, initRag, loaderLabels]);

  const invokeRag = useCallback((message: ChatMessage) => {
    setLoading(true);
    setHistory(curr => curr.concat(message));

    const body = {
      input: { question: message.text },
      thread_id: `${rag!.chats![0].id}`,
    }

    Llm.invokeRag(id!, body)
      .then(
        ({ data }) => {
          setLoading(false);
          const systemMessage: ChatMessage = {
            isUserPrompt: false,
            timestamp: new Date().toISOString(),
            text: data.answer.content,
          };
          setHistory(h => h!.concat(systemMessage));
        },
        (err) => {
          setLoading(false);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rag-invoke-error', message, `Invoke Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, rag, showToast]);

  const handleFileDialog = useCallback(() => {
    document.getElementById('file-upload-input')?.click();
  }, []);

  const toggleSettings = useCallback(() => setShowSettings(s => !s), []);
  const openCreateLoaderModal = useCallback(() => setShowCreateLoaderModal(true), []);
  const openEmbedLoaderModal = useCallback(() => setShowEmbedLoaderModal(true), []);

  const saveContextConfigs = useCallback(() => {
    showLoader();

    const body = ragConfigs!.configs.reduce((acc, curr) => {
      acc[curr.name] = curr.value || curr.default;
      return acc;
    }, {} as Record<string, any>);

    Llm.updateRagContext(id!, body)
      .then(
        ({ data }) => {
          hideLoader();
          showToast('rag-update-success', `${ragLabels.singular} context updated successfully`, `${ragLabels.singular} Success`, 'success');
          setRag(data);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rag-invoke-error', message, `Invoke Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast, showLoader, hideLoader, ragConfigs, ragLabels]);

  const handleContextChange = useCallback((path: string) => (evt: any) => {
    const value = evt.target?.value || evt;

    setRagConfigs(curr => {
      const newConfigs = cloneDeep(curr!);
      set(newConfigs!, path, value);
      return newConfigs;
    });
  }, []);

  const handleReloadLoader = useCallback((loader: EmbeddedLoader) => () => {
    setRag(curr => {
      const newRag = cloneDeep(curr!);
      const found = newRag.loaders!.findIndex(l => l.id === loader.id)!;
      newRag.loaders![found].status = undefined;
      return newRag;
    });

    Llm.reloadRagLoader(id!, loader.id!)
      .then(
        () => initRag(),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loader-reload-error', message, `${loaderLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    // eslint-disable-next-line
  }, [id, initRag, showToast]);

  const handleRemoveLoader = useCallback((loader: EmbeddedLoader) => () => {
    showLoader();

    Llm.removeRagLoader(id!, loader.id!)
      .then(
        () => {
          hideLoader();
          showToast('loader-remove-success', `${loaderLabels.singular} removed successfully`, `${loaderLabels.singular} Success`, 'success');
          initRag();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loader-remove-error', message, `${loaderLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    // eslint-disable-next-line
  }, [id, showToast, showLoader, hideLoader, initRag]);

  const handleSubmit = useCallback((loader: RagLoader) => {
    showLoader();

    Llm.createLoader(loader)
      .then(
        () => {
          hideLoader();
          showToast('loader-success', `${loaderLabels.singular} created successfully`, `${loaderLabels.singular} Success`, 'success');
          handleClose();
          reloadLoaders();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [showLoader, hideLoader, showToast, reloadLoaders, handleClose, loaderLabels]);

  const renderSettingsTab = useCallback((key: string) => {
    if (key === 'loaders') return (
      <div className="tab-flex-content">
        <Flex className="p-2 border-bottom">
          {!!rag?.loaders?.length && (
            <Button size="sm" variant="light" className="border" onClick={openEmbedLoaderModal}>
              <FontAwesomeIcon icon={solid('plus-circle')} />
              <span className="ms-2">Embed existing {loaderLabels.singular}</span>
            </Button>
          )}

          <Button size="sm" variant="success" className="ms-auto" onClick={openCreateLoaderModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Create new {loaderLabels.singular}</span>
          </Button>
        </Flex>

        <div className="tab-scroll-content">
          {rag?.loaders?.length === 0 && (
            <button className="add-loaders-alert" onClick={openEmbedLoaderModal}>
              <FontAwesomeIcon icon={solid('plus-circle')} size="lg" />
              <span className="mt-2">Add {loaderLabels.singular}</span>
            </button>
          )}

          {rag?.loaders?.map(loader => (
            <LoaderItem
              key={loader.id}
              loader={loader}
              embedded
              onReload={handleReloadLoader(loader)}
              onRemove={handleRemoveLoader(loader)}
              showActions={showEmbeddedLoaderActions}
            />
          ))}
        </div>
      </div>
    )

    if (key === 'context') return (
      <div className="tab-flex-content">
        <Flex className="p-2 border-bottom">
          <Button variant="success" size="sm" className="ms-auto" onClick={saveContextConfigs}>
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save Configuration</span>
          </Button>
        </Flex>

        <div className="tab-scroll-content">
          <p className="text-muted">{ragConfigs?.description}</p>

          {ragConfigs?.configs.map((config, index) => (
            <Form.Group key={index} controlId={`${config.name}-group`} className="mb-2">
              <Form.Label>{config.name}</Form.Label>
              {config.type === 'boolean'
                ? <Form.Check checked={config.value as boolean} onChange={handleContextChange(`config.[${index}].value`)} />
                : config.type === 'array'
                  ? <Select mode="tags" placeholder={config.default ? `${config.default}` : config.type} value={config.value as string[] || []} onChange={handleContextChange(`configs.[${index}].value`)} />
                  : <Form.Control size="sm" placeholder={config.default ? `${config.default}` : config.type} value={config.value as string || ""} onChange={handleContextChange(`configs.[${index}].value`)} />
              }
              <Form.Text>{config.description}</Form.Text>
            </Form.Group>
          ))}
        </div>
      </div>
    )
  }, [rag, ragConfigs, handleContextChange, openCreateLoaderModal, openEmbedLoaderModal, saveContextConfigs, handleReloadLoader, handleRemoveLoader, loaderLabels, showEmbeddedLoaderActions]);

  const renderChat = useCallback(() => {
    return (
      <Chat
        history={history}
        loading={loading}
        extra={rag!.loaders?.length === 0 && (
          <Alert variant="info" className="py-2 mb-2">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">No {loaderLabels.plural} found for this chat. The AI Bot will not be able to answer to specific topics without them.</span>
          </Alert>
        )}
        actions={
          <>
            <IconButton tooltip="Attache Files" onClick={handleFileDialog}>
              <FontAwesomeIcon icon={solid('paperclip')} />
            </IconButton>
            <IconButton className="ms-3" tooltip={(showSettings ? "Hide" : "Show") + " Settings"} onClick={toggleSettings}>
              <FontAwesomeIcon icon={showSettings ? solid('arrow-right') : solid('cog')} />
            </IconButton>
          </>
        }
        onMessageSend={invokeRag}
      />
    )
  }, [history, loading, invokeRag, showSettings, toggleSettings, handleFileDialog, rag, loaderLabels]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred. Try again later.</span>
      </Alert>
    )

    if (!rag) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return (
      <Row className="rag-chat-row">
        <Col xs={showSettings ? 9 : 12}>{renderChat()}</Col>

        <Col xs={3} className={classNames("settings-col", { show: showSettings })}>
          <div className="settings-container">
            <Tabs
              defaultActiveKey="loaders"
              items={
                settingsTabs!.map(tab => ({
                  ...tab,
                  children: renderSettingsTab(tab.key),
                }))
              }
            />
          </div>
        </Col>
      </Row>
    )
  }, [rag, error, showSettings, renderChat, renderSettingsTab, settingsTabs]);

  return (
    <Container fluid className="rag-chat mt-3">
      <Helmet>
        <title>Rag {id} Chat</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/llm/rags' }}>{ragLabels.plural} List</Breadcrumb.Item>
        {rag && <Breadcrumb.Item active>'{rag.name}' Chat</Breadcrumb.Item>}
      </Breadcrumb>

      {renderAsyncContent()}

      <EditLoaderModal loader={loader} open={showCreateLoaderModal} onClose={handleClose} onSubmit={handleSubmit} />

      <Modal
        open={showEmbedLoaderModal}
        onCancel={handleClose}
        onClose={handleClose}
        footer={null}
      >
        <h6>Embed {loaderLabels.singular}</h6>

        <br />

        {loaders?.map(loader => (
          <LoaderItem
            key={loader.id}
            loader={loader}
            onEmbed={embedLoader(loader)}
          />
        ))}
      </Modal>
    </Container>
  )
}

type Props = {
  onEmbed?: VoidFunction
  onReload?: VoidFunction;
  onRemove?: VoidFunction;
  showActions?: boolean;
} & ({
  embedded?: false;
  loader: RagLoader;
} | {
  embedded: true;
  loader: EmbeddedLoader;
})

const LoaderItem = memo(({ loader, onEmbed, onReload, onRemove, embedded, showActions }: Props) => {
  const loaderLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "loaders"));
  // -1 means loading
  const loaderPercent = useMemo(() => {
    if (!embedded) return;

    if (!loader.status || !loader.status.progress || !loader.status.total) return -1;

    return Math.floor(loader.status.progress * 100 / loader.status.total);
  }, [loader, embedded]);

  const renderProgress = useCallback(() => {
    if (!embedded || loaderPercent === undefined) return; // just to set the right type

    return (
      <div className="mt-1">
        <Tooltip key={loaderPercent < 0 ? 'loading' : 'progress'} title={loaderPercent < 0 ? 'Loading' : `${loaderPercent}%`}>
          {loaderPercent < 0
            ? <ProgressBar striped animated now={100} variant="secondary" style={{ height: '0.35rem' }} />
            : <ProgressBar now={loaderPercent} variant="primary" style={{ height: '0.35rem' }} />
          }
        </Tooltip>
      </div>
    )
  }, [embedded, loaderPercent]);

  return (
    <div key={loader.id} className="loader-item border rounded">
      <Flex align="center">
        <Col className="overflow-hidden">
          <h6 className="mb-0 text-truncate">{loader.name}</h6>
          <Badge bg="none" className="bg-pink">{loader.type}</Badge>
          <Tooltip
            title={
              <>
                <div>{loader.description}</div>
                {embedded && loader.embedDateTime && (
                  <div className="font-sm">
                    <span>Embedded on:</span>
                    <b className="ms-1">{formatDate(loader.embedDateTime)}</b>
                  </div>
                )}
              </>
            }
          >
            <FontAwesomeIcon icon={solid('info-circle')} className="ms-1 text-muted" />
          </Tooltip>
          {(embedded && (loaderPercent === 100 || loader.status?.finished)) && (
            <Tooltip title="Completed">
              <FontAwesomeIcon icon={solid('check-circle')} className="ms-1 text-success" />
            </Tooltip>
          )}
        </Col>

        {(showActions && embedded && (loaderPercent === 100 || loader.status?.finished)) && (
          <>
            <IconButton tooltip="Reload" onClick={onReload}>
              <FontAwesomeIcon icon={solid('refresh')} />
            </IconButton>
            <IconButton tooltip="Remove" className="ms-1" onClick={onRemove}>
              <FontAwesomeIcon icon={solid('trash')} className="text-danger" />
            </IconButton>
          </>
        )}

        {!embedded && (
          <IconButton onClick={onEmbed} tooltip={`Add ${loaderLabels.singular}`}>
            <FontAwesomeIcon icon={solid('plus')} size="lg" />
          </IconButton>
        )}
      </Flex>
      {embedded && (!loader.status?.finished || (loaderPercent && loaderPercent < 100)) && renderProgress()}
    </div>
  )
});

export default memo(RagChat);